import React, {useCallback, useEffect, useMemo, useState} from 'react';

import omit from 'lodash.omit';
import {WidthItem} from 'src/components/width-item';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {shouldUpdate} from '@pb/kit-util-should-update';


import {CHANGE_TYPE} from './constants';
import {RangeField} from './range-field';
import {RangeItem} from './range-item';
import {countErrors, createRange, getFieldValue, isCellValid, parseData, prepareData, validateRanges} from './utils';

import {apiV2IpRanges, apiV2IpRangesError} from './ip-ranges.module.styl';

import type {ChangeType} from './constants';
import type {Acc, OnCellChange, OnRemoveRange, OnUpdateRanges, RangesListDto, ValueAcc} from './typedef';

const ERROR_MESSAGE = 'Each value should be a number between 0 and 255';

type Props = {
    changeType?: ChangeType,
    isSingleAddress?: boolean,
    data: RangesListDto | null,
    rangesBusy: boolean,
    onUpdateRanges: OnUpdateRanges,
    disabled?: boolean,
};

export const IpRanges = (props: Props) => {
    const {rangesBusy, data, isSingleAddress, changeType = CHANGE_TYPE.ON_SAVE, onUpdateRanges, disabled} = props;

    const parsedData = useMemo(() => parseData(data), [data]);

    const [errorCount, setErrorCount] = useState<number>(0);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [fieldValue, setFieldValue] = useState<ValueAcc>(getFieldValue());
    const [acc, setAcc] = useState<Acc>(parsedData.acc);
    const [currentRanges, setCurrentRanges] = useState<RangesListDto>(parsedData.currentRanges);
    const [idsList, setIdsList] = useState<Array<string>>(parsedData.idsList);

    useEffect(() => {
        setIsChanged(false);
        setAcc(parsedData.acc);
        setCurrentRanges(parsedData.currentRanges);
        setIdsList(parsedData.idsList);

    }, [parsedData]);

    const onChange: OnCellChange = useCallback(({type, value}) => {
        const isValueValid = isCellValid(value);

        const nextFieldValue = {...fieldValue};
        nextFieldValue[type] = {
            value: value.trim(),
            isValid: isValueValid,
        };
        setErrorCount(countErrors(nextFieldValue));
        setFieldValue(nextFieldValue);
    }, [fieldValue]);

    const isChangeOnSave = useCallback(() => {
        return changeType === CHANGE_TYPE.ON_SAVE;
    }, [changeType]);

    const onRemove = useCallback<OnRemoveRange>(rangeId => {
        const nextAcc = omit(acc, rangeId);
        const nextIdsList = idsList.filter(id => id !== rangeId);
        const nextRanges = prepareData({acc: nextAcc, idsList: nextIdsList});

        if (!isChangeOnSave()) {
            onUpdateRanges(nextRanges);
        }

        setIsChanged(shouldUpdate(nextRanges, currentRanges, undefined));
        setIdsList(nextIdsList);
        setAcc(nextAcc);
    }, [acc, idsList, currentRanges, isChangeOnSave, onUpdateRanges]);

    const onAdd = useCallback(() => {
        const {nextValueAcc, range} = validateRanges({acc: fieldValue, isSingleAddress});
        const newErrorsCount = countErrors(nextValueAcc);

        if (newErrorsCount === 0) {
            const {rangeId, rangeData} = createRange(range);
            const nextAcc = {
                ...acc,
                [rangeId]: rangeData,
            };
            const nextIdsList = idsList.concat(rangeId);
            const nextRanges = prepareData({acc: nextAcc, idsList: nextIdsList});

            if (!isChangeOnSave()) {
                onUpdateRanges(nextRanges);
            }

            setFieldValue(getFieldValue());
            setIsChanged(shouldUpdate(nextRanges, currentRanges, undefined));
            setIdsList(nextIdsList);
            setAcc(nextAcc);
        } else {
            setFieldValue(nextValueAcc);
            setErrorCount(newErrorsCount);
        }

    }, [fieldValue, acc, idsList, currentRanges, isSingleAddress, isChangeOnSave, onUpdateRanges]);

    const onSave = useCallback(() => {
        onUpdateRanges(prepareData({acc, idsList}));
    }, [acc, idsList, onUpdateRanges]);

    const isError = errorCount > 0;
    const isSaveDisabled = !isChanged || disabled;
    const hasItems = idsList.length > 0;
    const isSave = isChangeOnSave() && (isChanged || hasItems);

    return (
        <div className={apiV2IpRanges}>
            <Group direction="v" gap="sm">
                {idsList.map(rangeId => (
                    <RangeItem
                        key={rangeId}
                        {...{
                            isSingleAddress,
                            rangesBusy,
                            rangeId,
                            data: acc[rangeId],
                            onRemove,
                            disabled,
                        }}
                    />
                ))}
                <RangeField
                    {...{
                        isSingleAddress,
                        data: fieldValue,
                        rangesBusy,
                        onChange,
                        disabled,
                    }}
                />
                {isError ? (
                    <span className={apiV2IpRangesError}>{ERROR_MESSAGE}</span>
                ) : null}
                <WidthItem width="100%">
                    <Group justify="space-between" gap={hasItems ? 'xs' : 'l'}>
                        <Button variant="Type.SECONDARY" disabled={rangesBusy || disabled} busy={!data} onClick={onAdd}>
                            Add
                        </Button>
                        {isSave ? (
                            <Button variant="Type.PRIMARY" busy={rangesBusy} disabled={isSaveDisabled} onClick={onSave}>
                                Save
                            </Button>
                        ) : null}
                    </Group>
                </WidthItem>
            </Group>
        </div>
    );
};
